import { ChainId } from '@traderjoe-xyz/joepegs-sdk'
import { DATA_API_URL } from 'constants/explorers'

const TRANSFERS_PAGE_SIZE = 20
const COLLECTIBLES_PAGE_SIZE = 500
const COLLECTION_TOKENS_PAGE_SIZE = 50

export interface DataApiNftMetadata {
  indexStatus: string
  animationUri?: string
  attributes?: string
  description?: string
  externalUrl?: string
  imageUri?: string
  metadataLastUpdatedTimestamp?: number
  name?: string
}

interface DataApiToken {
  address: string
  tokenId: string
  metadata?: DataApiNftMetadata
  name?: string
  symbol?: string
}

export type DataApiErcType = 'ERC-721' | 'ERC-1155'

export interface DataApiNftToken {
  address: string
  ercType: DataApiErcType
  tokenId: string
  tokenUri: string
  balance?: string
  metadata?: DataApiNftMetadata
  name?: string
  ownerAddress?: string
  symbol?: string
}

export interface DataApiCollectiblesPage {
  collectibleBalances: DataApiNftToken[]
  nextPageToken?: string
}

export interface DataApiCollectionTokensPage {
  tokens: DataApiNftToken[]
  nextPageToken?: string
}

export interface DataApiTransfer {
  blockNumber: string
  blockTimestamp: number
  from: { address: string }
  logIndex: number
  to: { address: string }
  txHash: string
  erc721Token?: DataApiToken
  erc1155Token?: DataApiToken
  value?: string
}

export interface DataApiTransferPage {
  transfers: DataApiTransfer[]
  nextPageToken?: string
}

interface DataApiQueryParams {
  pageSize?: number
  pageToken?: string
}

const buildUrl = (path: string, params: DataApiQueryParams): string => {
  const searchParams = new URLSearchParams()
  if (params.pageSize !== undefined) {
    searchParams.set('pageSize', String(params.pageSize))
  }
  if (params.pageToken) {
    searchParams.set('pageToken', params.pageToken)
  }
  const query = searchParams.toString()
  return query ? `${DATA_API_URL}${path}?${query}` : `${DATA_API_URL}${path}`
}

/** A non-2xx Data API response. `detail` is the message the API sent. */
export class DataApiError extends Error {
  readonly detail: string
  readonly status: number

  constructor(url: string, status: number, detail: string) {
    super(`Data API ${status} for ${url}: ${detail}`)
    this.name = 'DataApiError'
    this.detail = detail
    this.status = status
  }
}

export const isDataApiNotFound = (error: unknown): boolean =>
  error instanceof DataApiError && error.status === 404

const readErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body: unknown = await response.json()
    if (
      typeof body === 'object' &&
      body !== null &&
      'message' in body &&
      typeof body.message === 'string'
    ) {
      return body.message
    }
  } catch {
    // no JSON body: fall through to the status text
  }
  return `${response.status} ${response.statusText}`
}

const throwOnError = async (url: string, response: Response): Promise<void> => {
  if (!response.ok) {
    throw new DataApiError(
      url,
      response.status,
      await readErrorMessage(response)
    )
  }
}

const fetchDataApi = async <T>(
  path: string,
  params: DataApiQueryParams = {}
): Promise<T> => {
  const url = buildUrl(path, params)
  const response = await fetch(url)
  await throwOnError(url, response)
  return response.json()
}

const postDataApi = async (path: string): Promise<void> => {
  const url = buildUrl(path, {})
  const response = await fetch(url, { method: 'POST' })
  await throwOnError(url, response)
}

export const listCollectionTransfers = (
  chainId: ChainId,
  collection: string,
  pageToken?: string
): Promise<DataApiTransferPage> =>
  fetchDataApi<DataApiTransferPage>(
    `/chains/${chainId}/tokens/${collection}/transfers`,
    {
      pageSize: TRANSFERS_PAGE_SIZE,
      pageToken
    }
  )

interface DataApiTransactionPage {
  transactions: DataApiTransfer[]
  nextPageToken?: string
}

const listAddressTransfers = async (
  chainId: ChainId,
  address: string,
  method: 'listErc721' | 'listErc1155',
  pageToken?: string
): Promise<DataApiTransferPage> => {
  const page = await fetchDataApi<DataApiTransactionPage>(
    `/chains/${chainId}/addresses/${address}/transactions:${method}`,
    { pageSize: TRANSFERS_PAGE_SIZE, pageToken }
  )
  return { nextPageToken: page.nextPageToken, transfers: page.transactions }
}

export const listAddressErc721Transfers = (
  chainId: ChainId,
  address: string,
  pageToken?: string
): Promise<DataApiTransferPage> =>
  listAddressTransfers(chainId, address, 'listErc721', pageToken)

export const listAddressErc1155Transfers = (
  chainId: ChainId,
  address: string,
  pageToken?: string
): Promise<DataApiTransferPage> =>
  listAddressTransfers(chainId, address, 'listErc1155', pageToken)

export const listCollectibles = (
  chainId: ChainId,
  address: string,
  pageToken?: string
): Promise<DataApiCollectiblesPage> =>
  fetchDataApi<DataApiCollectiblesPage>(
    `/chains/${chainId}/addresses/${address}/balances:listCollectibles`,
    { pageSize: COLLECTIBLES_PAGE_SIZE, pageToken }
  )

export const listCollectionTokens = (
  chainId: ChainId,
  collection: string,
  pageToken?: string
): Promise<DataApiCollectionTokensPage> =>
  fetchDataApi<DataApiCollectionTokensPage>(
    `/chains/${chainId}/nfts/collections/${collection}/tokens`,
    { pageSize: COLLECTION_TOKENS_PAGE_SIZE, pageToken }
  )

export const getCollectionToken = (
  chainId: ChainId,
  collection: string,
  tokenId: string
): Promise<DataApiNftToken> =>
  fetchDataApi<DataApiNftToken>(
    `/chains/${chainId}/nfts/collections/${collection}/tokens/${tokenId}`
  )

export const reindexCollectionToken = (
  chainId: ChainId,
  collection: string,
  tokenId: string
): Promise<void> =>
  postDataApi(
    `/chains/${chainId}/nfts/collections/${collection}/tokens/${tokenId}:reindex`
  )
